import { PDFDocument, rgb } from "pdf-lib";

/**
 * Generate edited PDF with saved signature/text fields
 */
export const generateEditedPdf = async (
  fields,
  fileBlob,
  viewportInfo
) => {
  if (!fileBlob || !viewportInfo) {
    console.error("Missing fileBlob or viewportInfo");
    return null;
  }

  try {
    console.log("Starting PDF generation with fields:", fields);
    console.log("Viewport info:", viewportInfo);

    const existingPdfBytes = await fetch(fileBlob).then((res) =>
      res.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pages = pdfDoc.getPages();

    console.log("PDF loaded, pages count:", pages.length);

    // Embed standard font
    const font = await pdfDoc.embedFont("Helvetica");

    let processedFields = 0;

    for (const field of fields) {
      if (!field.saved) {
        console.log("Skipping unsaved field:", field.id);
        continue;
      }

      console.log("Processing field:", field);

      const page = pages[field.pageIndex];
      if (!page) {
        console.error("Page not found for index:", field.pageIndex);
        continue;
      }

      const { width: pdfWidth, height: pdfHeight } = page.getSize();

      // Account for 80% rendered width
      const renderedWidth = viewportInfo.renderedWidth * 0.8;
      const renderedHeight = viewportInfo.renderedHeight * 0.8;

      const scaleX = pdfWidth / renderedWidth;
      const scaleY = pdfHeight / renderedHeight;

      const pdfX = field.x * scaleX;
      const pdfY = pdfHeight - field.y * scaleY;

      // IMAGE FIELD
      if (field.imageData) {
        try {
          const base64Data = field.imageData.split(",")[1];
          if (!base64Data) {
            console.error("Invalid image data format");
            continue;
          }

          const imageBytes = Uint8Array.from(
            atob(base64Data),
            (c) => c.charCodeAt(0)
          );

          const signatureImage = await pdfDoc.embedPng(imageBytes);

          const imageWidth = 150 * scaleX;
          const imageHeight =
            (150 * signatureImage.height) /
            signatureImage.width *
            scaleY;

          page.drawImage(signatureImage, {
            x: pdfX,
            y: pdfY - imageHeight,
            width: imageWidth,
            height: imageHeight,
          });

          processedFields++;
        } catch (imageError) {
          console.error("Error embedding image:", imageError);
        }
      }

      // TEXT FIELD
      else if (field.textData) {
        try {
          const fontColor = field.fontColor || "#000000";

          const r = parseInt(fontColor.slice(1, 3), 16) / 255;
          const g = parseInt(fontColor.slice(3, 5), 16) / 255;
          const b = parseInt(fontColor.slice(5, 7), 16) / 255;

          const fontSize =
            parseInt(field.fontSize || "14") * scaleY;

          page.drawText(field.textData, {
            x: pdfX,
            y: pdfY,
            size: fontSize,
            font: font,
            color: rgb(r, g, b),
          });

          processedFields++;
        } catch (textError) {
          console.error("Error drawing text:", textError);
        }
      }
    }

    console.log("Processed fields:", processedFields);

    const pdfBytes = await pdfDoc.save();

    const blob = new Blob([pdfBytes], {
      type: "application/pdf",
    });

    return blob;
  } catch (error) {
    console.error("PDF generation error:", error);
    console.error("Error details:", {
      message: error.message,
      stack: error.stack,
      fields: fields,
      viewportInfo: viewportInfo,
    });

    throw error;
  }
};

/**
 * Convert Blob to Base64 (without data prefix)
 */
export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64data = reader.result.split(",")[1];
      resolve(base64data);
    };

    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });